import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { ApiService } from '@/_services/api.service';
import { EFeedback, IMessage } from '@/types';
import { currentConversationIdAtom } from '../atoms/conversationAtoms';
import { queryKeys } from '../queries/queryKeys';
import { AnalyticsService } from '@/_services/analytics.service.client';

interface UpdateFeedbackParams {
	messageId: string;
	feedback: EFeedback | null;
}

/**
 * Mutation hook to update message feedback (thumbs up/down)
 *
 * Uses optimistic updates for instant UI feedback via React Query cache.
 * Note: We don't invalidate queries because messages are loaded via streaming
 * and the query function returns empty array. We rely on the optimistic update
 * and server confirmation to keep cache in sync.
 */
export const useMessageFeedback = () => {
	const queryClient = useQueryClient();
	const conversationId = useAtomValue(currentConversationIdAtom);

	return useMutation({
		mutationFn: async ({ messageId, feedback }: UpdateFeedbackParams) => {
			if (!conversationId) {
				throw new Error('No conversation selected');
			}

			const response = await ApiService.UpdateMessageFeedback({
				conversationId,
				messageId,
				feedback
			});

			if (response.error) {
				throw new Error(response.error.message);
			}

			return response.data as IMessage;
		},
		onMutate: async ({ messageId, feedback }) => {
			if (!conversationId) return;

			// Cancel outgoing refetches
			await queryClient.cancelQueries({ queryKey: queryKeys.messages.byConversation(conversationId) });

			// Snapshot previous value
			const previousMessages = queryClient.getQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId));

			// Optimistically update the cache
			queryClient.setQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId), (old) =>
				old?.map((msg) => (msg.id === messageId ? { ...msg, feedback } : msg))
			);

			return { previousMessages };
		},
		onError: (_error, _variables, context) => {
			// Rollback on error
			if (context?.previousMessages && conversationId) {
				queryClient.setQueryData(queryKeys.messages.byConversation(conversationId), context.previousMessages);
			}
		},
		onSuccess: (updatedMessage) => {
			// Update the cache with the actual server response to ensure consistency
			if (conversationId) {
				queryClient.setQueryData<IMessage[]>(queryKeys.messages.byConversation(conversationId), (old) =>
					old?.map((msg) => (msg.id === updatedMessage.id ? { ...msg, feedback: updatedMessage.feedback } : msg))
				);

				// Track feedback event
				if (updatedMessage.feedback) {
					AnalyticsService.feedbackGiven({
						conversationId,
						messageId: updatedMessage.id,
						feedbackType: updatedMessage.feedback
					});
				}
			}
		}
		// Note: No onSettled invalidation - messages are streaming-based, not query-fetched
	});
};
